"use client";

import { useState } from "react";
import { useSession } from "next-auth/react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { CASE_NEWSLETTER } from "@/lib/tools/toolAlertsTexts";

/**
 * « Prévenez-moi si ça change », sous un outil dont la veille suit les sources.
 *
 * LE RÉSULTAT RESTE SANS COMPTE ET SANS ADRESSE. L'alerte est un service en
 * plus, jamais une condition : on peut calculer autant qu'on veut sans rien
 * laisser. L'adresse ne sert qu'à prévenir, et la newsletter n'est qu'une case
 * décochée, facultative et séparée.
 *
 * REPLIÉ TANT QU'ON NE LE DEMANDE PAS. Le bloc s'ouvre d'un clic, et c'est à ce
 * moment seulement que reCAPTCHA se charge : un visiteur qui calcule sans
 * s'abonner ne télécharge rien de Google.
 */

const TEXTES = {
    fr: {
        titre: "Prévenez-moi si ça change",
        sousTitre: "Un taux, un seuil ou un plafond de ce calcul change ? Nous vous écrivons, une fois le changement vérifié.",
        adresse: "Votre adresse e-mail",
        exemple: "vous@exemple.fr",
        bouton: "Me prévenir",
        envoi: "Un instant…",
        mention: "Il suffit de votre adresse, sans compte ni paiement. Vous recevez d'abord un e-mail de confirmation, et chaque envoi contient un lien pour arrêter.",
        aConfirmer: "C'est presque fini : ouvrez l'e-mail que nous venons d'envoyer à {email} et confirmez votre adresse.",
        actif: "C'est fait : nous vous écrirons à {email} si ce calcul change.",
        deja: "Vous êtes déjà prévenu pour cet outil.",
        erreurEmail: "Cette adresse ne semble pas valide.",
        erreurRecaptcha: "La vérification anti-robot n'a pas abouti. Réessayez dans un instant.",
        erreur: "L'inscription n'a pas abouti. Réessayez dans un instant.",
    },
    en: {
        titre: "Tell me if this changes",
        sousTitre: "A rate, threshold or ceiling behind this calculation changes? We write to you once the change is checked.",
        adresse: "Your email address",
        exemple: "you@example.com",
        bouton: "Notify me",
        envoi: "One moment…",
        mention: "Your address is all it takes, with no account and no payment. You get a confirmation email first, and every email has a link to stop.",
        aConfirmer: "Almost there: open the email we just sent to {email} and confirm your address.",
        actif: "Done: we will write to {email} if this calculation changes.",
        deja: "You are already on the list for this tool.",
        erreurEmail: "This address does not look valid.",
        erreurRecaptcha: "The anti-robot check did not go through. Please try again in a moment.",
        erreur: "It did not go through. Please try again in a moment.",
    },
};

export default function ToolsChangeAlert({ cleOutil, lang, integre = false }) {
    const T = TEXTES[lang] || TEXTES.fr;
    const [ouvert, setOuvert] = useState(false);

    // `integre` : une ligne de l'encart de confiance de la fiche, qui porte
    // déjà le cadre ; sinon, un encart à lui seul.
    return (
        <section className={integre ? "px-5 py-4" : "mt-8 rounded-xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] px-5 py-4"}>
            <button
                type="button"
                onClick={() => setOuvert(true)}
                aria-expanded={ouvert}
                className="flex w-full items-start gap-3 text-left"
            >
                <SolarIcon name="bell-linear" className="mt-0.5 h-5 w-5 shrink-0 text-gray-400 dark:text-lightBlue/60" />
                <span className="min-w-0">
                    <span className={`block font-semibold dark:text-white ${integre ? "text-sm" : ""}`}>{T.titre}</span>
                    <span className="mt-0.5 block text-sm text-gray-600 dark:text-lightBlue">{T.sousTitre}</span>
                </span>
            </button>

            {ouvert && (
                <GoogleReCaptchaProvider
                    reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY}
                    scriptProps={{ async: true, defer: true }}
                >
                    <Formulaire cleOutil={cleOutil} lang={lang} T={T} />
                </GoogleReCaptchaProvider>
            )}
        </section>
    );
}

function Formulaire({ cleOutil, lang, T }) {
    const { data: session } = useSession();
    const { executeRecaptcha } = useGoogleReCaptcha();
    // Tant que rien n'a été tapé, le champ montre l'adresse du compte, même si
    // la session n'arrive qu'après l'ouverture du bloc.
    const [saisie, setSaisie] = useState(null);
    const email = saisie ?? (session?.user?.email || "");
    const [newsletter, setNewsletter] = useState(false);
    const [etat, setEtat] = useState({ statut: "pret" });

    const envoyer = async (evenement) => {
        evenement.preventDefault();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            setEtat({ statut: "erreur", message: T.erreurEmail });
            return;
        }
        if (!executeRecaptcha) {
            setEtat({ statut: "erreur", message: T.erreurRecaptcha });
            return;
        }
        setEtat({ statut: "envoi" });
        try {
            const recaptchaToken = await executeRecaptcha("tools_alert");
            const reponse = await fetch("/api/tools/alerts/subscribe", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: email.trim(), cleOutil, newsletter, locale: lang, recaptchaToken }),
            });
            const donnees = await reponse.json().catch(() => ({}));
            if (!reponse.ok) {
                const message =
                    donnees.erreur === "email" ? T.erreurEmail : donnees.erreur === "recaptcha" ? T.erreurRecaptcha : T.erreur;
                setEtat({ statut: "erreur", message });
                return;
            }
            setEtat({ statut: donnees.etat, email: donnees.email });
        } catch {
            setEtat({ statut: "erreur", message: T.erreur });
        }
    };

    if (["a-confirmer", "actif", "deja"].includes(etat.statut)) {
        const texte = { "a-confirmer": T.aConfirmer, actif: T.actif, deja: T.deja }[etat.statut];
        return (
            <p role="status" className="mt-4 mb-0 flex items-start gap-2 text-sm text-gray-700 dark:text-lightBlue">
                <SolarIcon name="check-circle-linear" className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{texte.replace("{email}", etat.email || email)}</span>
            </p>
        );
    }

    return (
        <form onSubmit={envoyer} className="mt-4" noValidate>
            <label htmlFor={`alerte-${cleOutil}`} className="block text-sm font-semibold dark:text-white">
                {T.adresse}
            </label>
            <div className="mt-1.5 flex flex-col gap-2 sm:flex-row">
                <input
                    id={`alerte-${cleOutil}`}
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setSaisie(e.target.value)}
                    placeholder={T.exemple}
                    // Le bloc s'ouvre pour qu'on y tape son adresse : le focus
                    // y va directement, au lieu de rester sur le titre.
                    autoFocus
                    className="min-h-[44px] min-w-0 flex-1 rounded-lg border border-gray-200 dark:border-white/15 bg-white dark:bg-white/[0.04] px-3 py-2 text-sm dark:text-white focus:border-primary focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[#D04515]/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-bgDark-800"
                />
                <button
                    type="submit"
                    disabled={etat.statut === "envoi"}
                    className="inline-flex min-h-[44px] items-center justify-center shrink-0 rounded-lg bg-gray-900 dark:bg-white px-4 py-2 text-sm font-semibold text-white dark:text-gray-900 transition-opacity hover:opacity-90 disabled:opacity-60"
                >
                    {etat.statut === "envoi" ? T.envoi : T.bouton}
                </button>
            </div>
            <label className="mt-3 flex items-start gap-2 text-sm text-gray-600 dark:text-lightBlue cursor-pointer">
                <input
                    type="checkbox"
                    checked={newsletter}
                    onChange={(e) => setNewsletter(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span>{CASE_NEWSLETTER[lang] || CASE_NEWSLETTER.fr}</span>
            </label>
            {etat.statut === "erreur" && (
                <p role="alert" className="mt-2 mb-0 text-sm text-red-700 dark:text-red-400">
                    {etat.message}
                </p>
            )}
            <p className="mt-3 mb-0 text-xs leading-5 text-gray-500 dark:text-lightBlue/70">{T.mention}</p>
        </form>
    );
}
